import React, { useState, useEffect } from 'react';
import getConfig, { OMFE2ConfigModel, MarketsHeaderOptions } from '@om-fe/core/utils/config';
import { MatchListItemLayout, MAIN_MARKET_MIN_LIMIT } from '../../components/match-list/utils/match-list.constants';
import { DisciplineLiveStatus, MarketGroupOverviewModel } from '@om-fe/models';
import { PlatformTypes } from '@om-fe/utils';
import { GetMarketGroupsResponse } from '@om-fe/api';
import { RpcStateStatus, RpcState } from '@om-fe/widgets';
import TournamentMatchList from '../match-list/containers/tournament-match-list/tournament-match-list';
import { MatchListSkeleton } from '@om-fe/widgets/dist/widgets/match-list/match-list-skeleton';
import { FilteredMarketGroupsOverview } from '../match-list/utils/match-list-models';
import keyBy from 'lodash/keyBy';

const config: OMFE2ConfigModel = getConfig();
const matchListLayout: MatchListItemLayout = MatchListItemLayout.VirtualSport;
const marketsHeaderOptions: MarketsHeaderOptions = config?.marketsHeaderOptions || MarketsHeaderOptions.SHOW_TOURNAMENT;

const DEFAULT_LIMIT_NUMBER_OF_EVENTS: number = 100;

interface TournamentMatchListVirtualSportProps {
    readonly virtualSportsId: string;
    readonly hasMarketSelector: boolean;
    readonly mainMarketsLimit: number;
    readonly virtualTournamentId: string;
    readonly deviceType?: PlatformTypes;
    readonly marketGroupData: RpcState<GetMarketGroupsResponse, unknown>;
    readonly extraParam?: string;
}

export const TournamentMatchListVirtualSport = ({
    virtualSportsId,
    hasMarketSelector,
    mainMarketsLimit,
    virtualTournamentId,
    deviceType,
    marketGroupData,
    extraParam,
}: TournamentMatchListVirtualSportProps) => {
    const [groupOverview, setGroupOverview] = useState<string | undefined>(undefined);
    const [filteredMarketGroupsOverview, setFilteredMarketGroupsOverview] = useState<Record<string, FilteredMarketGroupsOverview>>();
    const [filteredMarketGroupsOverviewList, setFilteredMarketGroupsOverviewList] = useState<string[]>([]);

    useEffect(()=>{
        if (marketGroupData.status === RpcStateStatus.Success) {
            const { data } = marketGroupData;
            const marketGroupsOverviewData: MarketGroupOverviewModel[] | undefined = data.response;
            const filteredMarketGroupsOverviewData: Record<string, FilteredMarketGroupsOverview> = keyBy(marketGroupsOverviewData, 'id');
            const filteredMarketGroupsOverviewListData = marketGroupsOverviewData && marketGroupsOverviewData.length ? marketGroupsOverviewData.map(item => item.id) : [];
            const groupOverviewData = filteredMarketGroupsOverviewListData.slice(0, mainMarketsLimit).join(',');
            setFilteredMarketGroupsOverview(filteredMarketGroupsOverviewData);
            setFilteredMarketGroupsOverviewList(filteredMarketGroupsOverviewListData);
            setGroupOverview(groupOverviewData);
        }
    },[mainMarketsLimit, marketGroupData]);

    const selectTournamentGroupsOverview = (item: string) => {
        const listSelected = mainMarketsLimit === 1 ? item : filteredMarketGroupsOverviewList?.slice(0, mainMarketsLimit - 1).concat(item).join(',');
        setGroupOverview(listSelected);
    }

    if (
        marketGroupData.status === RpcStateStatus.Loading ||
        marketGroupData.status === RpcStateStatus.Initial ||
        marketGroupData.status === RpcStateStatus.Failed ||
        !groupOverview) {
        return <MatchListSkeleton />;
    }

    return (
         <TournamentMatchList
            instanceId={`VirtualTournament-MatchList-${virtualTournamentId}`}
            tournamentId={virtualTournamentId}
            liveStatus={DisciplineLiveStatus.BOTH}
            itemLayout={matchListLayout}
            limitNumberOfEvents={DEFAULT_LIMIT_NUMBER_OF_EVENTS}
            mainMarketsLimit={mainMarketsLimit ?? MAIN_MARKET_MIN_LIMIT}
            hasMarketSelector={hasMarketSelector}
            marketsHeaderOptions={marketsHeaderOptions}
            showMatchListHeader={false}
            showMarketInMatchListGroupHeader={true}
            groupOverview={groupOverview}
            deviceType={deviceType}
            disciplineId={virtualSportsId}
            isVirtual={true}
            filteredMarketGroupsOverview={filteredMarketGroupsOverview}
            filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
            selectGroupsOverview={selectTournamentGroupsOverview}
            extraParam={extraParam}
            preventRedirect={true}
        />
    )
}